// interfaces
import { ApiResponse } from "@payment-client/interfaces/Api";

// internal-deps
import { format } from "@swiggy-private/core-ui";
import {
    getBridgeSdk,
    GlobalHeaderState,
    NEWRELIC_TABLES,
    PlaceOrderData,
    recordNREvent,
    SwgyHeaderKeysList,
    cleanupPersistOrderDataInLocalStorage,
    CleanupSource,
} from "@swiggy-private/checkout-core";

// utils
import { isApiResponse } from "./ApiHelper";
import { clearWebviewRefreshSavedState } from "./Misc";
import { isAndroidPlatform } from "@payment-client/utils/Headers";

export const DEFAULT_ERROR_CODE = "1";
export const DEFAULT_ERROR_MESSAGE = "Received error while {0} order";
export const ORDER_FAILED_EVENT_NAME = "order_failed";

export enum ORDER_FAIL_INSTANCE {
    PLACE = "place",
    PROCESS = "process",
    CONFIRM = "confirm",
}

interface OrderFailedPayload {
    error: ApiResponse | Error;
    failInstance: ORDER_FAIL_INSTANCE;
    placeOrderData: PlaceOrderData;
    totalBill: string;
    orderId?: string;
}

export const sendOrderFailedEvent = (payload: OrderFailedPayload): void => {
    const { placeOrderData, error, failInstance, orderId, totalBill } = payload;
    let errorCode = DEFAULT_ERROR_CODE;
    let errorText = format(DEFAULT_ERROR_MESSAGE, failInstance);

    if (isApiResponse(error)) {
        errorCode = String(error.statusCode ?? DEFAULT_ERROR_CODE);
        errorText = `${errorText} - ${error.statusMessage}`;
    } else if (error instanceof Error && error.message != null) {
        errorText = `${errorText} - ${error.message}`;
    }

    const platform = GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) ?? "";

    try {
        cleanupPersistOrderDataInLocalStorage(CleanupSource.CONFIRM_ORDER_FAIL);
    } catch {
        // no-op
    }

    if (isAndroidPlatform(platform)) {
        // used to clear webview refresh state in app memory after payment failures
        clearWebviewRefreshSavedState();
    }

    try {
        recordNREvent({
            eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_JS_BRIDGE_OVERRIDE,
            name: "sendOrderFailedEvent",
            orderId: orderId || "",
        });
    } catch {
        /* No-op */
    }

    getBridgeSdk()
        .logClevertapEvent({
            eventName: ORDER_FAILED_EVENT_NAME,
            params: {
                orderId: orderId || "",
                totalBill: totalBill || "",
                errorCode: errorCode || "",
                errorText: errorText || "",
                paymentMethod: placeOrderData.paymentMethodData.paymentCode || "",
                businessLine: GlobalHeaderState.getHeader(SwgyHeaderKeysList.marketplaceBusinessLine) || "",
                marketplaceCategory: GlobalHeaderState.getHeader(SwgyHeaderKeysList.marketplaceCategory) || "",
                xChannel: GlobalHeaderState.getHeader(SwgyHeaderKeysList.xChannel) || "",
                timeStamp: new Date().toISOString(),
            },
        })
        .catch(() => void 0);

    getBridgeSdk()
        .orderFailedEvent({
            orderId,
            totalBill,
            errorCode,
            errorText,
            marketplaceBusinessLine: GlobalHeaderState.getHeader(SwgyHeaderKeysList.marketplaceBusinessLine) || "",
            marketplaceCategory: GlobalHeaderState.getHeader(SwgyHeaderKeysList.marketplaceCategory) || "",
            paymentMethodData: placeOrderData.paymentMethodData,
        })
        .catch(() => void 0);
};
